/**
 * commandFilter — pure matching/ranking helpers behind the ⌘K palette.
 *
 * Extracted from CommandPalette so the ranking rules are testable without
 * rendering the overlay: `matchesCommand` decides whether a nav command
 * survives the query, `rankHit` scores where a query lands in a field
 * (prefix beats mid-string; Infinity = no hit) so record matches sort by
 * how early the hit is. Both take an ALREADY-lowercased, trimmed query — the
 * palette normalises once per keystroke, not once per row.
 */

/** The searchable surface of a nav command (label + hint + hidden terms). */
export interface FilterableCommand {
  label: string;
  hint: string;
  /** Extra lowercase search terms so e.g. "kanban" finds Workflows. */
  keywords?: string;
}

/**
 * Substring filter over a nav command. Empty query matches everything
 * (the palette shows the full command list until the operator types).
 */
export function matchesCommand(c: FilterableCommand, q: string): boolean {
  if (!q) return true;
  return (
    c.label.toLowerCase().includes(q) ||
    c.hint.toLowerCase().includes(q) ||
    (c.keywords ?? '').includes(q)
  );
}

/**
 * Where does `q` hit inside `hay`? Lower = better (0 = prefix match),
 * Infinity = no hit / missing field. Callers `Math.min` across several
 * fields of a record to get its overall rank.
 */
export function rankHit(hay: string | null | undefined, q: string): number {
  if (!hay) return Infinity;
  const i = hay.toLowerCase().indexOf(q);
  return i < 0 ? Infinity : i;
}
